import { useContext } from 'react'
import { fetch, fetchMinimalCompanyInfo } from '../../helpers/fetch.helper'
import { rootDataResponseHandler } from '../../helpers/responseHandlers'
import AuthContext from '../../units/auth/AuthContext'
import AuthStorage from '../../units/auth/AuthStorage'
import FetchContext from './FetchContext'

export default function FetchProvider({ children, version }) {
    const authenticator = useContext(AuthContext)
    const authStorage = new AuthStorage()

    function contextualFetch(options) {
        const { headers, ...rest } = options

        const contextualOptions = {
            ...rest,
            headers: {
                ...headers,
                'X-Client-Version': version,
            },
        }
        return fetch(contextualOptions)
    }

    async function authenticate(data) {
        const { jwt, user } = await contextualFetch({
            method: 'post',
            path: `/auth/local`,
            data,
            responseHandler: rootDataResponseHandler,
            authorize: false,
        })
        await authenticator.updateAuthentication(jwt, user)
    }

    async function authenticatedFetch(options) {
        const token = authStorage.getAuthenticationToken()
        if (token != null) {
            const { headers, ...rest } = options
            const authenticatedOptions = {
                ...rest,
                headers: {
                    ...headers,
                    'Authorization': `Bearer ${token}`,
                },
            }
            return contextualFetch(authenticatedOptions)
        }
        else {
            throw new Error('Not authenticated. Cannot fetch.')
        }
    }

    async function fetchDocument(id) {
        return authenticatedFetch({
            path: `/documents/${id}`,
        })
    }

    async function fetchDocumentForDocumentPage(id) {
        const params = {
            validate: true,
            fields: [
                'id',
                'type',
                'kind',
                'editor',
                'currency',
                'schedule',
                'scheduled_date',
                'status',
            ],
            populate: {
                entry: {
                    fields: [
                        'id',
                        'state',
                    ],
                    populate: {
                        documents: {
                            fields: [
                                'id',
                                'kind',
                            ],
                        },
                    },
                },
                images: {
                    fields: [
                        'id',
                        'type',
                        'provenance',
                    ],
                    populate: {
                        file: {
                            fields: [
                                'id',
                                'url',
                            ],
                        },
                    },
                },
                check: {
                    fields: [
                        'id',
                        'number',
                        'bank',
                        'branch',
                        'account',
                        'currency',
                        // 'is_valid',
                        'createdAt',
                    ],
                    populate: {
                        payee: {
                            fields: [
                                'id',
                                'account_name',
                            ],
                        },
                    },
                },
                voucher27: {
                    fields: [
                        'id',
                        'barcode',
                        'asmachtaA',
                        'beneficiaryCode',
                        'asmachtaB',
                        'currency',
                        'isValid',
                        'createdAt',
                        'path',
                    ],
                },
                voucher38: {
                    fields: [
                        'id',
                        'barcode',
                        'beneficiaryCode',
                        // 'voucherType',
                        'isValid',
                        'createdAt',
                        'path',
                    ],
                },
                voucher42: {
                    fields: [
                        'id',
                        'barcode',
                        'beneficiaryCode',
                        // 'voucherType',
                        'isValid',
                        'createdAt',
                        'path',
                    ],
                },
            },
        }
        return authenticatedFetch({
            path: `/documents/${id}`,
            params,
            responseHandler: rootDataResponseHandler,
        })
    }

    async function fetchUser() {
        return authenticatedFetch({
            path: `/users/me`,
            responseHandler: rootDataResponseHandler,
        })
    }

    async function fetchOneCompany(id) {
        return authenticatedFetch({
            path: `/companies/${id}`,
        })
    }

    async function fetchCompanyName(id) {
        const company = await fetchOneCompany(id)
        return company.companyName
    }

    async function fetchEntriesForEntriesPage(company) {
        const params = {
            populate: {
                documents: {
                    fields: [
                        'id',
                        'type',
                    ],
                },
            },
            filters: {
                company: {
                    id: company.id,
                },
            },
        }
        const entries = await authenticatedFetch({
            path: `/entries`,
            params,
        })
        return entries
    }

    async function fetchEntriesForActivePage() {
        const params = {
            populate: {
                documents: {
                    fields: [
                        'id',
                        'type',
                    ],
                },
            },
            filters: {
                state: {
                    $in: [
                        'new',
                        'invalid',
                        'compiled',
                    ],
                },
            },
        }
        const entries = await authenticatedFetch({
            path: `/entries`,
            params,
        })
        return entries
    }

    async function fetchEntryForEntryPage(id) {
        const params = {
            fields: [
                'id',
                'state',
            ],
            populate: {
                documents: {
                    fields: [
                        'id',
                        'type',
                        'kind',
                        'editor',
                        'currency',
                        'schedule',
                        'scheduled_date',
                        'status',
                    ],
                    populate: {
                        images: {
                            fields: [
                                'id',
                                'type',
                            ],
                            populate: {
                                file: {
                                    fields: [
                                        'id',
                                        'url',
                                    ],
                                },
                            },
                        },
                        check: {
                            fields: [
                                'id',
                                'number',
                                'bank',
                                'branch',
                                'account',
                                'currency',
                                // 'is_valid',
                                'createdAt',
                            ],
                            populate: {
                                payee: {
                                    fields: [
                                        'id',
                                        'account_name',
                                    ],
                                },
                            },
                        },
                        voucher27: {
                            fields: [
                                'id',
                                'barcode',
                                'asmachtaA',
                                'beneficiaryCode',
                                'asmachtaB',
                                'currency',
                                // 'voucherType',
                                'isValid',
                                'createdAt',
                                'path',
                            ],
                        },
                        voucher38: {
                            fields: [
                                'id',
                                'barcode',
                                'beneficiaryCode',
                                // 'voucherType',
                                'isValid',
                                'createdAt',
                                'path',
                            ],
                        },
                        voucher42: {
                            fields: [
                                'id',
                                'barcode',
                                'beneficiaryCode',
                                // 'voucherType',
                                'isValid',
                                'createdAt',
                                'path',
                            ],
                        },
                        feedback_items: {
                            fields: [
                                'id',
                                'key',
                                'outcome',
                                'value',
                            ],
                        },
                    },
                },
                clones: {
                    fields: [
                        'id',
                    ],
                },
                original: {
                    fields: [
                        'id',
                    ],
                },
            },
        }
        return authenticatedFetch({
            path: `/entries/${id}`,
            params,
            responseHandler: rootDataResponseHandler,
        })
    }

    async function fetchOneEntry(id) {
        return authenticatedFetch({
            path: `/entries/${id}`,
        })
    }

    async function fetchCompaniesForCompaniesPage(search) {
        const searchParam = search ? { search } : null
        const params = {
            ...searchParam,
            populate: {
                entries: {
                    fields: [
                        'id',
                        'state',
                    ],
                },
            },
        }
        const companies = await authenticatedFetch({
            path: `/companies`,
            params,
        })
        return companies.results
    }

    async function fetchPostBankPayees() {
        const params = {
            populate: {
                fields: [
                    'id',
                    'account',
                    'account_name',
                ],
            },
        }
        const payess = await authenticatedFetch({
            path: `/post-bank/fiav-lines`,
            params,
        })
        return payess
    }

    async function fetchJournals() {
        const params = {
            sort: ['createdAt:desc'],
            fields: [
                'id',
                'type',
                'createdAt',
            ],
            populate: {
                parent: {
                    fields: ['id'],
                },
                parameters: {
                    fields: ['name', 'value'],
                },
                entries: {
                    fields: ['id', 'state'],
                    populate: {
                        company: { fields: ['id', 'companyName'] },
                    },
                },
                documents: {
                    fields: ['id', 'type'],
                    populate: {
                        voucher27: { fields: ['path'] },
                        voucher38: { fields: ['path'] },
                        voucher42: { fields: ['path'] },
                        entry: {
                            fields: ['id', 'state'],
                            populate: {
                                company: { fields: ['id', 'companyName'] },
                            },
                        },
                    },
                },
                images: {
                    fields: ['id', 'type'],
                    populate: {
                        document: {
                            fields: ['id', 'type'],
                            populate: {
                                voucher27: { fields: ['path'] },
                                voucher42: { fields: ['path'] },
                                entry: {
                                    fields: ['id', 'state'],
                                    populate: {
                                        company: { fields: ['id', 'companyName'] },
                                    },
                                },
                            },
                        },
                    },
                },
                batches: {
                    fields: ['id', 'name'],
                },
            },
        }
        return authenticatedFetch({
            path: `/journals`,
            params,
        })
    }

    async function fetchAnnouncements() {
        const params = {
            status: 'draft',
            sort: ['createdAt:desc'],
            fields: [
                'name',
                'notes',
                'isContentLocked',
                'frequency',
                'startAt',
                'endAt',
                'totalViewings',
                'totalViewingDuration',
                'totalConversions',
                'totalUniqueConversions',
            ],
        }

        return authenticatedFetch({
            path: `/announcements`,
            params,
        })
    }

    async function fetchAnnouncementsForCurrentUser() {
        return authenticatedFetch({
            path: `/users/me/announcements`,
            responseHandler: rootDataResponseHandler,
        })
    }

    // The editor works on the draft: the published version is what users already
    // have, and content is locked the moment it exists.
    async function fetchAnnouncement(documentId) {
        const params = {
            status: 'draft',
            populate: {
                image: true,
                type: true,
            },
        }

        return authenticatedFetch({
            path: `/announcements/${documentId}`,
            params,
        })
    }

    // The preview shows an announcement as a recipient would see it, so it reads the
    // draft — what the editor is working on — rather than the published version.
    async function fetchAnnouncementForPreview(documentId) {
        const params = {
            status: 'draft',
            fields: [
                'title',
                'caption',
                'moreInfoText',
                'bodyText',
                'conversionButtonText',
                'conversionMessage',
            ],
            populate: {
                image: true,
                type: true,
            },
        }

        return authenticatedFetch({
            path: `/announcements/${documentId}`,
            params,
        })
    }

    async function fetchAnnouncementStats(documentId) {
        return authenticatedFetch({
            path: `/announcements/${documentId}/stats`,
        })
    }

    async function fetchFirms() {
        return authenticatedFetch({
            path: `/firms`,
        })
    }

    async function fetchFirm(firmId) {
        return authenticatedFetch({
            path: `/firms/${firmId}`,
            responseHandler: rootDataResponseHandler,
        })
    }

    // The own-firm reads name no firm: the server resolves it from the token, so there is
    // nothing in the request that could point at another firm.
    async function fetchOwnFirmSettings() {
        return authenticatedFetch({
            path: `/users/me/firm/settings`,
            responseHandler: rootDataResponseHandler,
        })
    }

    async function fetchOwnFirmSenderNames() {
        return authenticatedFetch({
            path: `/users/me/firm/sender-names`,
            responseHandler: rootDataResponseHandler,
        })
    }

    async function fetchOwnFirmContactDetails() {
        return authenticatedFetch({
            path: `/users/me/firm/contact-details`,
            responseHandler: rootDataResponseHandler,
        })
    }

    async function fetchUsers() {
        return authenticatedFetch({
            path: `/users`,
        })
    }

    async function fetchOneUser(userId) {
        return authenticatedFetch({
            path: `/users/${userId}`,
        })
    }

    async function fetchEmails() {
        return authenticatedFetch({
            path: `/emails`,
        })
    }

    async function fetchEmail(emailId) {
        return authenticatedFetch({
            path: `/emails/${emailId}`,
        })
    }

    async function fetchReplies() {
        return authenticatedFetch({
            path: `/users/me/replies`,
        })
    }

    async function fetchReply(replyId) {
        return authenticatedFetch({
            path: `/users/me/replies/${replyId}`,
        })
    }

    // The only read here that does not authenticate: the recipient following an
    // unsubscribe link has no account. Retrieving it must not change anything, so the
    // confirming POST is a mutation and does not belong on this provider.
    async function fetchUnsubscribe(token) {
        return contextualFetch({
            path: `/public/unsubscribe/${token}`,
            authorize: false,
        })
    }

    function isAuthenticated() {
        return authStorage.isAuthenticated()
    }

    const fetcher = {
        fetch: contextualFetch,
        authenticatedFetch,
        fetchDocument,
        fetchDocumentForDocumentPage,
        fetchUser,
        fetchOneCompany,
        fetchCompanyName,
        fetchMinimalCompanyInfo,
        fetchOneEntry,
        fetchEntryForEntryPage,
        fetchCompaniesForCompaniesPage,
        fetchEntriesForEntriesPage,
        fetchEntriesForActivePage,
        fetchPostBankPayees,
        fetchJournals,
        fetchAnnouncements,
        fetchAnnouncementsForCurrentUser,
        fetchAnnouncement,
        fetchAnnouncementForPreview,
        fetchAnnouncementStats,
        fetchFirms,
        fetchFirm,
        fetchOwnFirmSettings,
        fetchOwnFirmSenderNames,
        fetchOwnFirmContactDetails,
        fetchUsers,
        fetchOneUser,
        fetchEmails,
        fetchEmail,
        fetchReplies,
        fetchReply,
        fetchUnsubscribe,
        authenticate,
        isAuthenticated,
    }

    return (
        <FetchContext.Provider value={fetcher}>
            {children}
        </FetchContext.Provider>
    )
}
